import { z } from 'zod';

// ==========================================
// 1. CURRENCIES & RATES SCHEMAS
// ==========================================

export const CurrencyCodeSchema = z.enum([
  'USD',
  'ARS_BLUE',
  'ARS_OFICIAL',
  'ARS_TARJETA',
  'ARS_MEP',
  'BRL',
  'PYG',
]);

export type CurrencyCode = z.infer<typeof CurrencyCodeSchema>;

export const CurrencyMetadataSchema = z.object({
  code: CurrencyCodeSchema,
  symbol: z.string(),
  name: z.string(),
  shortName: z.string(),
  flag: z.string(),
  locale: z.string(),
  fractionDigits: z.number().int().min(0).max(4),
});

export const SingleRateSchema = z.object({
  code: CurrencyCodeSchema,
  buy: z.number().positive(),
  sell: z.number().positive(),
  spread: z.number().nonnegative().optional(),
  variation24h: z.number().optional(),
  updatedAt: z.string().datetime(),
  source: z.string(),
});

export const ExchangeRatesResponseSchema = z.object({
  success: z.boolean(),
  baseCurrency: z.literal('USD'),
  timestamp: z.string().datetime(),
  circuitBreakerState: z.enum(['CLOSED', 'HALF_OPEN', 'OPEN', 'FALLBACK']),
  rates: z.object({
    ARS_BLUE: SingleRateSchema,
    ARS_OFICIAL: SingleRateSchema,
    ARS_TARJETA: SingleRateSchema,
    ARS_MEP: SingleRateSchema,
    BRL: SingleRateSchema,
    PYG: SingleRateSchema,
    USD: SingleRateSchema,
  }),
  metadata: z.object({
    cached: z.boolean(),
    stale: z.boolean(),
    provider: z.string(),
    ttl: z.number(),
  }),
});

export type ExchangeRatesResponse = z.infer<typeof ExchangeRatesResponseSchema>;
export type SingleRate = z.infer<typeof SingleRateSchema>;

// ==========================================
// 2. STORES & MERCHANTS OF CIUDAD DEL ESTE
// ==========================================

export const StoreIdSchema = z.enum([
  'nissei',
  'cellshop',
  'mega_eletronicos',
  'shopping_china',
  'madrid_center',
  'visovilar',
  'mobile_zone',
  'atacado_games',
  'topdek',
  'elegancia_company',
  'oneclick',
  'flytec',
  'multipass',
  'matrix',
  'digital_center',
  'nave',
  'ict',
  'pioneer',
  'audiovideo',
]);

export type StoreId = z.infer<typeof StoreIdSchema>;

export const StoreInfoSchema = z.object({
  id: StoreIdSchema,
  name: z.string().min(1),
  displayName: z.string(),
  logoUrl: z.string().url(),
  domain: z.string(),
  allowedDomains: z.array(z.string()),
  shoppingMall: z.string(),
  address: z.string(),
  floor: z.string().optional(),
  googleMapsUrl: z.string().url().optional(),
  rating: z.number().min(0).max(5),
  acceptsPix: z.boolean().default(true),
  acceptsCards: z.boolean().default(true),
  acceptsCashUsd: z.boolean().default(true),
  acceptsCashArs: z.boolean().default(true),
  cardSurchargePercentage: z.number().min(0).max(20).default(0), // Tarjetas en CDE suelen recargar entre 3% y 8%
  taxFreeAvailable: z.boolean().default(false),
  verifiedPartner: z.boolean().default(true),
});

export type StoreInfo = z.infer<typeof StoreInfoSchema>;

// ==========================================
// 3. PRODUCT & OFFERS SCHEMAS
// ==========================================

export const StockStatusSchema = z.enum([
  'in_stock',
  'low_stock',
  'out_of_stock',
  'pre_order',
]);

export type StockStatus = z.infer<typeof StockStatusSchema>;

export const StoreOfferSchema = z.object({
  storeId: StoreIdSchema,
  storeName: z.string(),
  priceUSD: z.number().positive(),
  originalPriceUSD: z.number().positive().optional(),
  discountPercentage: z.number().min(0).max(100).optional(),
  stockStatus: StockStatusSchema,
  stockQuantity: z.number().int().min(0).optional(),
  productUrl: z.string().url(),
  warrantyMonths: z.number().int().min(0).default(12),
  paymentMethodsSupported: z.array(z.string()).default(['CASH_USD', 'PIX', 'CREDIT_CARD']),
  lastChecked: z.string().datetime(),
});

export type StoreOffer = z.infer<typeof StoreOfferSchema>;

export const ProductCategorySchema = z.enum([
  'smartphones',
  'informatica',
  'tablets',
  'consolas_gaming',
  'audio_tv',
  'smart_tv',
  'camaras_foto',
  'perfumeria_belleza',
  'relojes_smartwatch',
  'electrodomesticos',
  'drones_movilidad',
  'bebidas',
  'herramientas',
  'deportes',
  'ar_condicionado',
  'acessorios_autos',
]);

export type ProductCategory = z.infer<typeof ProductCategorySchema>;

export const ProductSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  title: z.string().min(2),
  brand: z.string().min(1),
  category: ProductCategorySchema,
  subcategory: z.string().optional(),
  model: z.string().optional(),
  mpn: z.string().optional(),
  ean: z.string().optional(),
  description: z.string(),
  images: z.array(z.string().url()).min(1),
  thumbnail: z.string().url(),
  specifications: z.record(z.string(), z.string()).default({}),
  bestPriceUSD: z.number().positive(),
  highestPriceUSD: z.number().positive(),
  bestStoreId: StoreIdSchema,
  offersCount: z.number().int().min(1),
  offers: z.array(StoreOfferSchema).min(1),
  arcaCategory: z.enum(['tech_electronics', 'clothing_luggage', 'perfume_cosmetics', 'general']).default('tech_electronics'),
  rating: z.number().min(0).max(5).default(4.8),
  reviewsCount: z.number().int().min(0).default(0),
  isFeatured: z.boolean().default(false),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Product = z.infer<typeof ProductSchema>;

// ==========================================
// 4. ARCA / AFIP CUSTOMS SCHEMAS
// ==========================================

export const BorderTransportTypeSchema = z.enum([
  'terrestrial_river', // $300 USD franquicia terrestre/fluvial (Puente de la Amistad / Tancredo Neves)
  'air_maritime',      // $500 USD franquicia aérea (Aeropuerto Cataratas / Foz)
]);

export type BorderTransportType = z.infer<typeof BorderTransportTypeSchema>;

export const CustomsCalculationInputSchema = z.object({
  transportType: BorderTransportTypeSchema.default('terrestrial_river'),
  adultsCount: z.number().int().min(1).max(20).default(1),
  minorsUnder16Count: z.number().int().min(0).max(20).default(0),
  itemsTotalUSD: z.number().min(0),
  isDutyFreeAirportPurchase: z.boolean().default(false),
  dutyFreeSpentUSD: z.number().min(0).default(0),
});

export type CustomsCalculationInput = z.infer<typeof CustomsCalculationInputSchema>;

export const CustomsCalculationResultSchema = z.object({
  transportType: BorderTransportTypeSchema,
  individualAdultFranchiseUSD: z.number(),
  individualMinorFranchiseUSD: z.number(),
  totalFranchiseUSD: z.number(),
  itemsTotalUSD: z.number(),
  taxableExcessUSD: z.number(),
  dutyRatePercentage: z.literal(50),
  dutyToPayUSD: z.number(),
  dutyToPayARS: z.number(),
  officialRateBNA: z.number(),
  isExempt: z.boolean(),
  notes: z.array(z.string()),
});

export type CustomsCalculationResult = z.infer<typeof CustomsCalculationResultSchema>;

// ==========================================
// 5. QUERY PARAMS & FILTER SCHEMAS
// ==========================================

export const SortOptionSchema = z.enum([
  'relevance',
  'price_asc',
  'price_desc',
  'discount_desc',
  'rating_desc',
  'offers_desc',
]);

export type SortOption = z.infer<typeof SortOptionSchema>;

export const ProductFiltersSchema = z.object({
  q: z.string().optional(),
  category: ProductCategorySchema.optional(),
  brand: z.string().optional(),
  minPrice: z.coerce.number().min(0).optional(),
  maxPrice: z.coerce.number().min(0).optional(),
  store: StoreIdSchema.optional(),
  inStockOnly: z.coerce.boolean().optional().default(false),
  sort: SortOptionSchema.optional().default('relevance'),
  page: z.coerce.number().int().min(1).optional().default(1),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  currency: CurrencyCodeSchema.optional().default('USD'),
});

export type ProductFilters = z.infer<typeof ProductFiltersSchema>;

// ==========================================
// 6. SAFE OUTBOUND REDIRECT SCHEMA (/api/out)
// ==========================================

export const SafeRedirectQuerySchema = z.object({
  url: z
    .string()
    .url('URL inválida o malformada')
    .refine((val) => val.startsWith('https://'), {
      message: 'Solo se permiten URLs seguras con protocolo https://',
    }),
  storeId: StoreIdSchema,
  prodId: z.string().optional(),
  utm_source: z.string().optional().default('comprasparaguay.com.ar'),
  utm_medium: z.string().optional().default('price_comparison'),
  utm_campaign: z.string().optional().default('cde_direct'),
  token: z.string().optional(),
});

export type SafeRedirectQuery = z.infer<typeof SafeRedirectQuerySchema>;
